import { Button, Css, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { createBidPackagesEditUrl, createBidPackagesUrl } from "src/RouteUrls";
import { StepActions, useStepperContext } from "src/components/stepper";
import { BidPackageStatus, BidScopeSource, SaveBidPackageInput } from "src/generated/graphqlTypes";
import {
  useOverlappingBidPackagesLazyQuery,
  useSaveBidPackagesMutation,
} from "src/routes/bids/add-bid-package/AddBidPackagePage.generated";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { isDefined } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { BidPackageFormState, mapBidPackagesToForm } from "../AddBidPackagePage";

export function BidPackageStepActions({ formState }: { formState: ObjectState<BidPackageFormState> }) {
  const { currentStep, nextStep, steps, isLastStep } = useStepperContext();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });
  const { openModal } = useModal();
  const [overlappingBidPackages] = useOverlappingBidPackagesLazyQuery();
  const [saveBidPackages] = useSaveBidPackagesMutation();
  const currentStepIndex = steps.findIndex((step) => step.value === currentStep.value);
  const nextStepDisabled = steps[currentStepIndex + 1]?.disabled;
  const history = useHistory();
  const saveBids = useCallback(
    async (exiting = false) => {
      const input = mapToSaveBidPackagesInput(formState);
      async function save() {
        const result = await saveBidPackages({ variables: { input } });

        const bidPackages = result.data?.saveBidPackages.bidPackages;
        if (bidPackages && !exiting) {
          // Reinitialize the formState
          formState.set(mapBidPackagesToForm(bidPackages));

          // Update the url to be edit
          const groupId = bidPackages[0].bidPackageGroup?.id;
          if (groupId) {
            // Use window.history to prevent a rerendering from happening
            window.history.replaceState(null, "", createBidPackagesEditUrl(groupId, projectId));
          }
        }

        if (exiting) {
          history.push(createBidPackagesUrl(projectId));
        } else {
          nextStep();
        }
      }
      const newBidPackages = input.filter((bp) => !isDefined(bp.id));
      // If we are creating new bid packages we should check if there are any overlapping bid packages
      if (newBidPackages.nonEmpty) {
        const { costType, projects, scopeSource } = newBidPackages.first!;
        const costCodes = newBidPackages.flatMap((bp) => bp.costCodes!);
        const { data } = await overlappingBidPackages({
          variables: { costCodes, costType: costType!, projects: projects!, scopeSource: scopeSource! },
        });

        // If we have overlapping bid packages we should display the confirmation modal
        if (data?.overlappingBidPackages.bidPackages.nonEmpty) {
          openModal({
            content: (
              <ConfirmationModal
                confirmationMessage={
                  <>
                    There are already bids for this pairing. Are you sure you want to continue?
                    <ul css={Css.mt3.$}>
                      {data.overlappingBidPackages.bidPackages.map((bp) => (
                        <li key={bp.id}>{`${bp.name} (${bp.projects.map((p) => p.name).join(", ")})`}</li>
                      ))}
                    </ul>
                  </>
                }
                onConfirmAction={save}
                title="Bid Already Exists"
                label="Yes"
              />
            ),
          });
          return;
        }
      }

      await save();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formState, nextStep],
  );

  const sendToTrades = useCallback(
    async () => {
      const { bidPackages } = formState.value;
      await saveBidPackages({
        variables: { input: bidPackages.map((bp) => ({ id: bp.id, status: BidPackageStatus.Open })) },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formState],
  );

  return (
    <StepActions>
      <>
        <Button variant="secondary" label="Save Draft & Exit" size="lg" onClick={() => saveBids(true)} />
        {!isLastStep ? (
          <Button label="Continue" size="lg" onClick={() => saveBids()} disabled={nextStepDisabled} />
        ) : (
          <Button
            label="Save"
            size="lg"
            onClick={async () => {
              await sendToTrades();
              history.push(createBidPackagesUrl(projectId));
            }}
            disabled={!formState.valid}
          />
        )}
      </>
    </StepActions>
  );
}

function mapToSaveBidPackagesInput(formState: ObjectState<BidPackageFormState>): SaveBidPackageInput[] {
  const { bidPackages, projects, marketId, isUnitBased, scopeSource, ...formStateVals } = formState.value;
  return bidPackages.map((bidPackage) => ({
    ...formStateVals,
    ...bidPackage,
    scopeSource,
    isHistoricalBid: !formStateVals.isInternalEstimate,
    // Both market-level and community-level bid packages use the projects field
    projects: marketId && (projects ?? []).isEmpty ? [marketId] : (projects ?? []),
    // If the scope source isn't product offering the package has to be unit based
    isUnitBased: (isDefined(scopeSource) && scopeSource !== BidScopeSource.ProductOffering) || isUnitBased,
  }));
}
